import {
  getSnapshot, applySnapshot, addDisposer, types, flow,
} from 'mobx-state-tree';
import { reaction } from 'mobx';
import * as Sentry from '@sentry/browser';
import {
  RootStore as ModelBase, VisitLatLngDtoModelType, VisitDtoModel, VisitLocationDtoModel, LocationStatus,
  RouteTemplatesDtoModel, RouteTemplatesConstraintDtoModel, RouteTemplatesDtoModelType,
  OrganizationOptimizationPreferencesDtoModel, OrganizationOptimizationPreferencesDtoModelType,
  VisitDtoModelType,
  PlanningActionDtoModelType,
  planningActionDtoModelPrimitives,
  RoleNames,
  SsoProvider,
  SsoClientApps,
  RoutePaginatedDtoModelType,
  RouteTimelineDtoModelType,
  RouteCalendarSummaryDtoModelType,
} from './models';
import getAppRoot from './utils/getAppRoot';
import getOrdersProps from './utils/getOrdersProps';
import RoutePaginatedQuery from './queries/routes/RoutePaginatedQuery';
import EditedVisitQuery from './queries/visits/EditedVisitQuery';
import {
  CreateRouteTemplatesDto,
  PatchRouteTemplatesDto,
  AddOrderDto,
  PatchProjectVisitDto,
  NewPatchRouteDto,
  CreateRouteDto,
  IGeolocationDto,
  PatchDriverDto,
  PatchOptimizationPreferencesDto,
  SignUpOrganizationArgs,
  Coordinates,
  AddOrdersMetadataDto,
} from './models/RootStore.base';
import { RouteDtoModelType } from './models/RouteDtoModel';
import DriverQuery from './queries/drivers/DriverQuery';
import RouteOverviewQuery from './queries/routes/RouteOverviewQuery';
import RouteCreatedQuery from './queries/routes/RouteCreatedQuery';
import RouteTemplatesOverviewQuery from './queries/routes/RouteTemplatesOverviewQuery';
import RoutesStatsQuery from './queries/routes/RoutesStatsQuery';
import RouteTemplatesQuery from './queries/routes/RouteTemplatesQuery';
import VisitLatLngQuery from './queries/visits/VisitLatLngQuery';
import { logglyLogger } from '../../services/logglyLogger';
import OptimizationPreferencesQuery from './queries/projects/OptimizationPreferencesQuery';
import ProjectIdQuery from './queries/projects/ProjectIdQuery';
import PublishSummaryQuery from './queries/projects/PublishSummaryQuery';
import RoutesWeekPaginatedQuery from './queries/routes/RoutesWeekPaginatedQuery';
import RoutesCalendarSummaryQuery from './queries/routes/RoutesCalendarSummaryQuery';
import RoutePaginatedExtraQuery from './queries/routes/RoutePaginatedExtraQuery';
import RoutePaginatedPublishStatusQuery from './queries/routes/RoutePaginatedPublishStatusQuery';
import OrgMemberUserQuery from './queries/user/OrgMemberUserQuery';
import SignupQuery from './queries/user/Signup';
import UserInviteQuery from './queries/user/UserInviteQuery';
import getAppEnv from './utils/getAppEnv';
import { formatPhoneWithCountryCode, normalizePhoneNumber } from '../../helpers/forms/phoneUtils';
import OrderTimelineQuery from './queries/visits/OrderTimelineQuery';
import { getOrderTimelineEventsBySequenceAscendingly } from './utils/getOrderTimelineEventsBySequenceAscendingly';
import { getLatestStopSequence } from './utils/getLatestStopSequence';
import { isValidDateRange } from './utils/dateRangeValidation';
import isValidUuid from '../../helpers/isValidUuid';
import convertDurationToSeconds from '../../helpers/convertDurationToSeconds';

const ROUTES_PAGE_SIZE = 25;

let activeRoutesFetch: AbortController | null = null;

const Model = ModelBase
  .props({
    isWorkspaceRouteLoading: types.optional(types.boolean, true),
    isBackgroundRoutesFetching: types.optional(types.boolean, false),
    isUnscheduledVisitsLatLngsLoading: types.optional(types.boolean, true),
    // Orders nav badge: the workspace-wide unscheduled total and the workspace it was fetched
    // for. The badge only shows the count while the tag matches the displayed workspace, so a
    // workspace switch hides the old number without the count ever being cleared.
    unscheduledOrdersTotalCount: types.maybeNull(types.number),
    unscheduledOrdersCountWorkspaceId: types.maybeNull(types.string),
    customFields: types.array(types.string),
    isSigningUp: types.optional(types.boolean, false),
    loadingOrderTimeline: types.optional(types.boolean, false),
  })
  .views(self => ({
    get isWorkspaceLoading(): boolean {
      return self.isUnscheduledVisitsLatLngsLoading || self.isWorkspaceRouteLoading;
    },
    // Orders nav badge count for the workspace the nav is showing; 0 (hidden) while the stored
    // count belongs to another workspace or hasn't been fetched yet.
    get unscheduledOrdersCountForDisplayedWorkspace(): number {
      const displayedWorkspaceId = getAppRoot(self).ui.displayedWorkspace?.id;
      if (displayedWorkspaceId == null || self.unscheduledOrdersCountWorkspaceId !== displayedWorkspaceId) {
        return 0;
      }
      return self.unscheduledOrdersTotalCount ?? 0;
    },
    getOrdersProps(orders: VisitDtoModelType[]) {
      return getOrdersProps(orders);
    },
  }))
  .views((self) => ({
    // Share one cache scan across all selected orders whose route timelines are not loaded.
    get orderTimelineEventsByOrderAndSolution(): Map<string, Map<string, RouteTimelineDtoModelType[]>> {
      const index = new Map<string, Map<string, RouteTimelineDtoModelType[]>>();
      self.routeTimelineDtos.forEach(event => {
        const { orderUuid, routeSolutionId } = event;
        if (!orderUuid || routeSolutionId == null) return;

        let solutions = index.get(orderUuid);
        if (!solutions) {
          solutions = new Map();
          index.set(orderUuid, solutions);
        }
        const solutionId = String(routeSolutionId);
        const events = solutions.get(solutionId);
        if (events) {
          events.push(event);
        } else {
          solutions.set(solutionId, [event]);
        }
      });
      return index;
    },
    get routesRange(): Map<string, RouteDtoModelType[]> {
      // Scope reads to the current workspace: `routeDtos` is workspace-agnostic, so a
      // late fetch from a workspace we left can re-merge after the reset-on-switch.
      // Routes with no projectId (optimistic local ones) stay visible.
      const currentProjectId = getAppRoot(self).ui.selectedWorkspace?.id;
      return Array.from<RouteDtoModelType>(self.routeDtos.values()).reduce((map, route) => {
        if (
          currentProjectId != null
          && route.projectId != null
          && String(route.projectId) !== String(currentProjectId)
        ) {
          return map;
        }
        if (map.has(route.startDate)) {
          map.get(route.startDate).push(route);
        } else {
          map.set(route.startDate, [route]);
        }
        return map;
      }, new Map());
    },
    get scopedVisits(): VisitDtoModelType[] {
      // Same read-time workspace guard as `routesRange`.
      const currentProjectId = getAppRoot(self).ui.selectedWorkspace?.id;
      return Array.from<VisitDtoModelType>(self.visitDtos.values()).filter((visit) => (
        currentProjectId == null
        || visit.projectId == null
        || String(visit.projectId) === String(currentProjectId)
      ));
    },
    get calendarRoutesRange(): Map<string, RouteCalendarSummaryDtoModelType[]> {
      return Array.from<RouteCalendarSummaryDtoModelType>(self.routeCalendarSummaryDtos.values()).reduce((map, route) => {
        if (map.has(route.startDate)) {
          map.get(route.startDate).push(route);
        } else {
          map.set(route.startDate, [route]);
        }
        return map;
      }, new Map());
    },
    orderTimelineEvents(orderUuid: string): RouteTimelineDtoModelType[] {
      const orderTimeline = Array.from<RouteTimelineDtoModelType>(self.routeTimelineDtos.values()).filter(r => r.orderUuid === orderUuid);
      return getOrderTimelineEventsBySequenceAscendingly(orderTimeline);
    },
    latestStopSequence(stopUuid: string): number | null | undefined {
      const routeTimelineDtos = Array.from<RouteTimelineDtoModelType>(getAppRoot(self).model.routeTimelineDtos.values());
      return getLatestStopSequence(stopUuid, getAppRoot(self).ui.selectedRoute, routeTimelineDtos);
    },
    routeByUuid(uuid: string): RouteDtoModelType | undefined {
      return Array.from<RouteDtoModelType>(self.routeDtos.values()).find((r) => r.uuid === uuid);
    },
  }))
  .actions((self) => ({
    resetLoading() {
      self.isWorkspaceRouteLoading = true;
      self.isBackgroundRoutesFetching = false;
    },
    setIsBackgroundRoutesFetching(value: boolean) {
      self.isBackgroundRoutesFetching = value;
    },
  }))
  .actions((self) => ({
    ssoSignupUser: flow(function* (
      authorizationCode: string,
      ssoProvider: SsoProvider,
      organization?: SignUpOrganizationArgs,
      fullname?: string,
      userInviteUuid?: string,
      gclid?: string,
    ) {
      self.isSigningUp = true;
      try {
        const signupData = yield self.mutateSsoSignUp({
          authorizationCode,
          ssoProvider,
          ssoClientApp: SsoClientApps.DISPATCHER_APP,
          organization,
          userInviteUuid,
          ...(fullname?.trim() && { fullname: fullname.trim() }),
          gclid,
        }, SignupQuery);

        self.isSigningUp = false;

        return signupData;
      } catch (error) {
        self.isSigningUp = false;
      }
    }),
    sendSignupVerificationCode: flow(function* (email: string) {
      self.isSigningUp = true;
      try {

        const { createVerificationCode } = yield self.mutateCreateVerificationCode({ email });

        self.isSigningUp = false;

        return createVerificationCode?.success;
      } catch (error) {
        self.isSigningUp = false;
        throw (error);
      }
    }),
    resendVerificationCode: flow(function* (email: string) {
      self.isSigningUp = true;
      try {

        const { resendVerificationCode } = yield self.mutateResendVerificationCode({ email });

        self.isSigningUp = false;

        return resendVerificationCode?.success;
      } catch (error) {
        self.isSigningUp = false;
        throw (error);
      }
    }),
    checkVerificationCode: flow(function* (email: string, verificationCode: string) {
      const { checkVerificationCode } = yield self.queryCheckVerificationCode({
        email,
        verificationCode,
      });

      return checkVerificationCode;
    }),
    signUpWithVerificationCode: flow(function* (email: string, fullname: string, password: string, verificationCode: string, organization: SignUpOrganizationArgs, gclid?: string) {
      self.isSigningUp = true;
      try {
        const signupData = yield self.mutateSignUpWithVerificationCode({
          email,
          fullname,
          password,
          verificationCode,
          organization,
          gclid,
        }, SignupQuery);

        self.isSigningUp = false;
        return signupData;
      } catch (error) {
        self.isSigningUp = false;
        throw (error);
      }
    }),
    signUpUser: flow(function* (fullname: string, email: string, password: string, organization: SignUpOrganizationArgs, gclid?: string) {
      self.isSigningUp = true;
      try {
        let signupData;

        signupData = yield self.mutateSignUp({
          fullname,
          email,
          password,
          organization,
          gclid,
        }, SignupQuery);

        self.isSigningUp = false;
        return signupData;

      } catch (error) {
        self.isSigningUp = false;
      }

    }),
    signUpUserWithInvite: flow(function* (fullname: string, email: string, password: string, userInviteUuid: string) {
      self.isSigningUp = true;
      let signupData;
      try {


        signupData = yield self.mutateSignUpWithInvite({
          fullname,
          email,
          password,
          userInviteUuid,
        }, SignupQuery);

        self.isSigningUp = false;

        return signupData;

      } catch (error) {
        self.isSigningUp = false;
        throw (error);
      }
    }),
    validateUserInviteToken: flow(function* (token: string) {
      try {
        const res = yield self.mutateValidateUserInviteToken({
          token,
        }, UserInviteQuery);

        return res.validateUserInviteToken;
      } catch (error) {
      }
    }),
    applyGeocodedVisits(projectId: string, visits: VisitLatLngDtoModelType[]): void {
      visits.forEach((v) => {
        const {
          locationId, visitId, latitude, longitude,
        } = getSnapshot(v);

        if (locationId && visitId) {
          const currentVisit = self.visitDtos.get(visitId) || {};

          self.visitLocationDtos.set(locationId, VisitLocationDtoModel.create({
            id: locationId,
            latitude,
            longitude,
            active: true,
            locationStatusId: LocationStatus.Validated,
            bestMatch: true,
          }));

          self.visitDtos.set(visitId, VisitDtoModel.create({
            ...currentVisit,
            id: visitId,
            projectId: Number(projectId),
            locations: [locationId],
          } as any));
        }
      });
    },
    fetchUnscheduledVisitsLatLngs: flow(function* (workspaceId: string) {
      const pageSize = 250;
      const selectedDate = getAppRoot(self).ui.selectedDate;

      self.isUnscheduledVisitsLatLngsLoading = true;

      const fetchVisitsPaginated = async (projectId: string, offset: number, deliveryDate?: string): Promise<any> => {
        const res = await self.queryVisitsPaginated({
          projectId,
          locationStatus: [LocationStatus.Validated, LocationStatus.Invalid, LocationStatus.Geocoding],
          scheduled: false,
          limit: pageSize,
          offset,
          deliveryDate,
        }, (model) => (model.totalCount.hasNextPage.edges((edge) => edge.node(VisitLatLngQuery))), { fetchPolicy: 'no-cache' }).promise;

        if (res.visitsPaginated.hasNextPage && getAppRoot(self).ui.selectedWorkspace?.id === projectId) {
          return fetchVisitsPaginated(projectId, offset + pageSize, deliveryDate);
        }

        return Promise.resolve();
      };

      yield fetchVisitsPaginated(workspaceId, 0, selectedDate);

      self.isUnscheduledVisitsLatLngsLoading = false;
    }),
    fetchUnscheduledOrdersTotalCount: flow(function* (workspaceId: string) {
      try {
        const res = yield self.queryVisitsPaginated({
          projectId: workspaceId,
          locationStatus: [LocationStatus.Validated, LocationStatus.Invalid, LocationStatus.Geocoding],
          scheduled: false,
          limit: 1,
          offset: 0,
        }, (qb) => qb.totalCount, { fetchPolicy: 'no-cache' }).currentPromise();

        // A late reply for a workspace the nav has since left would tag the count with a
        // workspace nothing is displaying, hiding the badge until the next refetch — drop it.
        if (getAppRoot(self).ui.displayedWorkspace?.id === workspaceId) {
          self.unscheduledOrdersTotalCount = res.visitsPaginated.totalCount ?? 0;
          self.unscheduledOrdersCountWorkspaceId = workspaceId;
        }
      } catch {
        // Keep the last known count; the next change-triggered refetch corrects it.
      }
    }),
    fetchProjectRoutes: flow(function* (projectId: string, startDate?: string, { background = false } = {}) {
      const appRoot = getAppRoot(self);
      const resolvedStartDate = startDate || appRoot.ui.selectedDate;

      if (!resolvedStartDate) {
        console.error('NO_START_DATE in fetchProjectRoutes', resolvedStartDate);
        Sentry.captureException(new Error('NO_START_DATE in fetchProjectRoutes'), {
          extra: { resolvedStartDate, projectId },
        });
        return Promise.reject(new Error('NO_START_DATE'));
      }

      const fetchPage = async (offset: number, limit: number): Promise<any> => {
        return new Promise<any>((resolve, reject) => {
          Sentry.getCurrentHub().withScope((scope) => {
            const transaction = Sentry.startTransaction({ name: 'queryRoutesPaginated' });
            scope.setSpan(transaction);
            self.queryRoutesPaginated({
              projectId,
              offset,
              limit,
              startDate: resolvedStartDate,
            }, RoutePaginatedQuery, { fetchPolicy: 'no-cache' }).currentPromise().then((res) => {
              transaction?.finish();
              resolve(res);
            }).catch((err) => {
              reject(err);
            });
          });
        });
      };

      // A background fetch must still own the loading flag if it aborts a foreground
      // fetch, or the flag stays stuck on true.
      const ownsLoadingFlag = !background || self.isWorkspaceRouteLoading;

      activeRoutesFetch?.abort();
      self.isBackgroundRoutesFetching = false;
      const controller = new AbortController();
      activeRoutesFetch = controller;

      const fetchRemaining = async (offset: number) => {
        if (controller.signal.aborted) return;
        try {
          const res = await fetchPage(offset, ROUTES_PAGE_SIZE);
          if (controller.signal.aborted) return;
          if (res.routesPaginated.hasNextPage && getAppRoot(self).ui.selectedWorkspace?.id === projectId) {
            await fetchRemaining(offset + ROUTES_PAGE_SIZE);
          }
        } catch {
          logglyLogger.log({ error: 'Background route fetch failed', info: `project ${projectId}, offset ${offset}` });
        }
      };

      try {
        if (ownsLoadingFlag) self.isWorkspaceRouteLoading = true;

        const firstPage = yield fetchPage(0, ROUTES_PAGE_SIZE);

        // A superseded fetch (a newer date change aborted this one) must not touch
        // the loading flag — the newer fetch owns it now. Clearing it here would
        // open the schedule gate while the new day's routes are still loading,
        // letting an empty route set masquerade as "this day has no routes".
        if (controller.signal.aborted) return Promise.resolve();

        if (ownsLoadingFlag) self.isWorkspaceRouteLoading = false;

        // A (re)load of the day's routes is a "settle" point for the timeline's departure-time
        // order: this is the moment the dispatcher expects rows to re-sort (day change,
        // optimisation finished, schedule-all), as opposed to their own in-flight edits.
        getAppRoot(self).timeline.settleRouteOrder();

        if (firstPage.routesPaginated.hasNextPage && getAppRoot(self).ui.selectedWorkspace?.id === projectId) {
          self.setIsBackgroundRoutesFetching(true);
          fetchRemaining(ROUTES_PAGE_SIZE).finally(() => {
            if (activeRoutesFetch === controller) {
              self.setIsBackgroundRoutesFetching(false);
              // Later pages arrived after the first settle. Slot them in without re-sorting rows the
              // dispatcher may already be editing.
              getAppRoot(self).timeline.mergeNewRoutesIntoSettledOrder();
            }
          });
        }
      } catch (err) {
        // Same guard on the error path: a superseded fetch failing must leave the
        // newer fetch's loading/background state alone.
        if (controller.signal.aborted) return Promise.reject();
        if (ownsLoadingFlag) self.isWorkspaceRouteLoading = false;
        self.isBackgroundRoutesFetching = false;
        return Promise.reject();
      }

      return Promise.resolve();
    }),
    fetchProjectRoutesExtraData: flow(function* (projectId: string, startDate?: string) {
      const appRoot = getAppRoot(self);
      const resolvedStartDate = startDate || appRoot.ui.selectedDate;

      if (!resolvedStartDate) {
        console.error('NO_START_DATE in fetchProjectRoutesExtraData', resolvedStartDate);
        Sentry.captureException(new Error('NO_START_DATE in fetchProjectRoutesExtraData'), {
          extra: { resolvedStartDate, projectId },
        });
        return Promise.reject(new Error('NO_START_DATE'));
      }

      const publishedRouteUuids: string[] = [];
      const fetchPaginated = async (fetchProjectId: string, offset: number): Promise<any> => {
        const pageSize = 25;
        const res = await new Promise<{
          routesPaginated: RoutePaginatedDtoModelType;
        }>((resolve, reject) => {
          Sentry.getCurrentHub().withScope((scope) => {
            const transaction = Sentry.startTransaction({ name: 'queryRoutesPaginatedExtra' });
            scope.setSpan(transaction);
            self.queryRoutesPaginated({
              projectId: fetchProjectId,
              offset,
              limit: pageSize,
              startDate: resolvedStartDate,
            }, RoutePaginatedExtraQuery, { fetchPolicy: 'no-cache' }).then((response) => {
              transaction?.finish();
              publishedRouteUuids.push(...response.routesPaginated.edges?.filter((row) => row.node.isDispatched).map((row) => row.node.uuid) ?? []);
              resolve(response);
            }, reject);
          });
        });

        if (res.routesPaginated.hasNextPage && getAppRoot(self).ui.selectedWorkspace?.id === projectId) {
          return fetchPaginated(projectId, offset + pageSize);
        }
        return Promise.resolve();
      };

      try {
        yield fetchPaginated(projectId, 0);
      } catch (err) {
        return Promise.reject();
      }

      return publishedRouteUuids;
    }),
    // Minimal fetch backing the nav's unpublished-routes badge on pages the
    // full route fetch doesn't cover (orders, drivers, non-workspace pages).
    fetchRoutesPublishStatus: flow(function* (projectId: string, startDate: string) {
      const fetchPage = async (offset: number): Promise<void> => {
        const res = await self.queryRoutesPaginated({
          projectId,
          offset,
          limit: ROUTES_PAGE_SIZE,
          startDate,
        }, RoutePaginatedPublishStatusQuery, { fetchPolicy: 'no-cache' }).currentPromise();
        if (res.routesPaginated.hasNextPage && getAppRoot(self).ui.displayedWorkspace?.id === projectId) {
          await fetchPage(offset + ROUTES_PAGE_SIZE);
        }
      };

      try {
        yield fetchPage(0);
      } catch {
        logglyLogger.log({ error: 'fetchRoutesPublishStatus failed', info: `project ${projectId}` });
      }
    }),
  }))
  .actions((self) => {
    let initialSnapshot: {};

    return {
      afterCreate() {
        initialSnapshot = getSnapshot(self);

        addDisposer(self, reaction(
          () => getAppRoot(self).auth.isAuthenticated,
          (isAuthenticated) => {
            if (!isAuthenticated) {
              applySnapshot(self, {
                ...initialSnapshot,
              });
            }
          },
        ));
      },

      // Drop routes whose `startDate` is in `datesToEvict`. The preview evicts only the
      // dates it fetched (never the global date), keeping the blast radius small. The
      // try/catch guards against MST throwing on an in-flight structural change.
      evictRoutesForDates(datesToEvict: string[]) {
        if (datesToEvict.length === 0) return;
        const evictSet = new Set(datesToEvict);
        const idsToRemove: string[] = [];
        self.routeDtos.forEach((route, id) => {
          if (route.startDate && evictSet.has(route.startDate)) {
            idsToRemove.push(id);
          }
        });
        idsToRemove.forEach((id) => {
          try {
            self.routeDtos.delete(id);
          } catch {
            // Leave the route in place if MST refuses the delete mid-flight.
          }
        });
      },

      resetModelForDate() {
        self.resetLoading();
        self.routesStatsDtos.clear();
        self.routeDtos.clear();
        self.routeConstraintDtos.clear();
        self.routeTimelineEtaDtos.clear();
        self.routeTimelineLogDtos.clear();
        self.routeTimelineDtos.clear();
        self.routeSolutionDtos.clear();
        self.visitDtos.clear();
      },

      resetModelForProject() {
        self.driverDtos.clear();
        self.routeDtos.clear();
        self.routesStatsDtos.clear();
        self.visitDtos.clear();
        self.routeConstraintDtos.clear();
        self.routeTemplatesDtos.clear();
        self.routeTimelineEtaDtos.clear();
        self.routeTimelineLogDtos.clear();
        self.visitLocationDtos.clear();
        self.visitTimeWindowDtos.clear();
        self.routeTimelineDtos.clear();
        self.routeSolutionDtos.clear();
        self.solutionUnscheduledDtos.clear();
        self.visitLocationDtos.clear();
        self.solutionStatusOnlyDtos.clear();
        self.assignActionStatusDtos.clear();
      },
    };
  })
  .actions((self) => ({
    addRouteTemplate(content: CreateRouteTemplatesDto) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      return appRoot.model.mutateCreateRouteTemplate({ projectId, routeTemplate: content }, RouteTemplatesQuery).currentPromise();
    },
    deleteRouteTemplate: flow(function* (templateId: string) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        throw new Error('NO_PROJECT_ID');
      }

      const deleteTemplate = async (id: string): Promise<any> => {
        await appRoot.model.mutateDeleteRouteTemplate({ projectId, id }, RouteTemplatesQuery);
        return Promise.resolve();
      };

      yield deleteTemplate(templateId);

      // Since GraphQL returns the item we deleted, we need to manually purge (or use a subscription update)
      self.routeTemplatesDtos.delete(templateId);
    }),
    deleteProject: flow(function* (projectId: string) {
      const appRoot = getAppRoot(self);

      if (!projectId) {
        throw new Error('NO_PROJECT_ID');
      }

      const deleteProject = async (id: string): Promise<any> => {
        await appRoot.model.mutateDeleteProject({ id }, ProjectIdQuery);
        return Promise.resolve();
      };

      yield deleteProject(projectId);

      getAppRoot(self).ui.setWorkspaceAsDeleted(projectId);
    }),
    updateRouteTemplate(id: string, patchContent: PatchRouteTemplatesDto) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      return appRoot.model.mutatePatchRouteTemplate(
        { projectId, id, patchContent },
        RouteTemplatesQuery,
        () => {
          // Optimistic update for UI; If backend call fails, this will automatically revert
          const template = self.routeTemplatesDtos.get(id);

          // You can only update 1 property at a time, so they will be top level or in constraints
          if (patchContent.constraints && template?.constraints?.id) {
            // Exclude tags from optimistic update — tags are string[] in the mutation input
            // but RouteTagDtoModel[] in the MST model. The server response will update them.
            const { tags, ...constraintsWithoutTags } = patchContent.constraints;
            self.routeTemplatesConstraintDtos.set(template.constraints.id, RouteTemplatesConstraintDtoModel.create({
              id: template.constraints.id,
              startTime: template.constraints.startTime,
              endTime: template.constraints.endTime,
              startLocations: [{
                ...template.constraints.activeStartLocation,
              }],
              endLocations: [{
                ...template.constraints.activeEndLocation,
              }],
              capacity: template.constraints.capacity,
              tags: template.constraints.tags ? getSnapshot(template.constraints.tags) : undefined,
              ...constraintsWithoutTags,
              routeTemplateId: template.constraints.routeTemplateId != null ? template.constraints.routeTemplateId : undefined,
            }));
          } else {
            self.routeTemplatesDtos.set(id, RouteTemplatesDtoModel.create({
              ...template,
              ...patchContent,
            } as RouteTemplatesDtoModelType));
          }
        },
      ).currentPromise();
    },
    patchOptimizationPreferences(patchContent: PatchOptimizationPreferencesDto) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      return appRoot.model.mutatePatchOptimizationPreferences(
        { patchContent, projectId },
        OptimizationPreferencesQuery,
        () => {
          let projectKey = '';
          self.organizationOptimizationPreferencesDtos.forEach((value, key) => {
            if (value.projectId === Number(projectId)) { projectKey = key; }
          });
          if (projectKey !== '') {
            const currentPreferences = self.organizationOptimizationPreferencesDtos.get(projectKey);
            self.organizationOptimizationPreferencesDtos.set(projectKey, OrganizationOptimizationPreferencesDtoModel.create({
              ...currentPreferences,
              ...patchContent,
            } as OrganizationOptimizationPreferencesDtoModelType));
          }
        },
      ).currentPromise();
    },
    searchSupportOrganization: flow(function* (searchValue: string) {
      const appRoot = getAppRoot(self);
      const isUuid = isValidUuid(searchValue);
      
      // Determine query parameters based on input type
      const queryParams = isUuid ? { userUuid: searchValue } : { email: searchValue };
      
      // Single call to get user data
      const userResult = yield self.queryGetUserAsAdmin(
        queryParams,
        qb => qb.id.uuid.fullname.email.roles(r => r.id.name).organization(o => o.id.name.uuid),
        { fetchPolicy: 'no-cache' },
      ).currentPromise();
      
      const userData = userResult.getUserAsAdmin;
      if (!userData || !userData.email) {
        throw new Error('User not found');
      }
      
      const userEmail = userData.email;
      
      // Now get the support organization using the email
      const result = yield self.querySupportOrganization({ userEmail }, qb => qb.name.uuid.dispatchers(d => d.uuid.email), { fetchPolicy: 'no-cache' }).currentPromise();

      const supportOrg = result.supportOrganization;
      if (supportOrg && userData) {
        appRoot.ui.setSupportOrganization({
          name: supportOrg.name,
          uuid: supportOrg.uuid,
          dispatchers: supportOrg.dispatchers
            .filter((d: any) => Boolean(d.email))
            .map((d: any) => ({ email: d.email, uuid: d.uuid })),
          userData,
        });
      }
      return supportOrg;
    }),
    createNewOrders(orders: AddOrderDto[], metadata?: AddOrdersMetadataDto) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      return appRoot.model.mutateCreateOrders({ orders, projectId, metadata }, qb => qb.id.deliveryDate).currentPromise();
    },
    deleteVisit(uuid: string) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      return appRoot.model.mutateDeleteProjectVisit({ uuid, projectId }, qb => qb.actionUuid).currentPromise();
    },
    bulkDeleteUnscheduledVisits(uuids: string[]) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      return appRoot.model.mutateBulkDeleteUnscheduledVisits({ uuids, projectId }, qb => qb
        .success(s => s.id.uuid)
        .error(e => e.uuid.reason)
        .totalSuccess
        .totalError)
        .currentPromise();
    },
    bulkEditUnscheduledVisits(uuids: string[], eventChanges: PatchProjectVisitDto) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      return appRoot.model.mutateBulkUpdateUnscheduledVisits({ eventChanges, uuids, projectId }, qb => qb
        .success(s => s.id.uuid)
        .error(e => e.uuid.reason)
        .totalSuccess
        .totalError)
        .currentPromise();
    },
    createOrder(order: AddOrderDto, metadata?: AddOrdersMetadataDto) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      return appRoot.model.mutateCreateOrders({ orders: [order], projectId, metadata }, qb => qb.id.uuid.deliveryDate)
        .currentPromise()
        .then(result => result.createOrders[0])
        .catch((error) => {
          logglyLogger.log({
            error: 'Failed to create order',
            info: `project ${projectId}`,
          });
          Sentry.captureException(error, {
            extra: { projectId, order, metadata },
          });
          return Promise.reject(error);
        });
    },
    shouldReoptimize(orderUuids: string[], targetRouteUuids: string[]): Promise<{ shouldReoptimize: boolean | undefined; forceReoptimize?: boolean | null } | undefined> {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      const checkReoptimize = async () => {
        const res = await self.queryShouldReoptimizeTargetRoutes(
          { projectId, eventUuids: orderUuids, targetRouteUuids },
          (b) => b.shouldReoptimize.forceReoptimize,
        ).currentPromise();
        return {
          shouldReoptimize: res.shouldReoptimizeTargetRoutes.shouldReoptimize,
          forceReoptimize: res.shouldReoptimizeTargetRoutes.forceReoptimize,
        };
      };

      return checkReoptimize();
    },
    unscheduleStops(eventUuids: string[]) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      if (!eventUuids) {
        return Promise.reject(new Error('NO_EVENT_UUID'));
      }

      return appRoot.model.mutateUnscheduleStopsFromRoutes({
        projectId, eventUuids,
      }, qb => qb.actionUuid.actionName).currentPromise().then((res) => {
        appRoot.mapView.revealStops(eventUuids);
        return res;
      });
    },
    rearrangeStopWithinRoute(routeUuid: string | undefined, eventUuid: string | undefined, targetVisitIndex: number) {
      const appRoot = getAppRoot(self);

      const projectId = appRoot.ui.selectedWorkspace?.id;
      if (!projectId) {
        logglyLogger.log({ error: 'No Project ID', info: `Failed to rearrange stop within route ${routeUuid}` });
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      if (!routeUuid) {
        logglyLogger.log({ error: 'No Route Uuid', info: `Failed to rearrange stop within route on project ${projectId}` });
        return Promise.reject(new Error('NO_ROUTE_UUID'));
      }

      if (!eventUuid) {
        logglyLogger.log({ error: 'No Event Uuid', info: `Failed to rearrange stop within route ${routeUuid} on project ${projectId}` });
        return Promise.reject(new Error('NO_EVENT_UUID'));
      }

      return new Promise((resolve, reject) => {
        Sentry.getCurrentHub().withScope((scope) => {
          const transaction = Sentry.startTransaction({ name: 'rearrangeStopWithinRoute' });
          scope.setSpan(transaction);
          appRoot.model.mutateMoveStopWithinRoute_new({
            projectId, routeUuid, eventUuid, targetVisitIndex,
          }, qb => qb.actionUuid.actionName).currentPromise().then((res) => {
            transaction.finish();
            appRoot.mapView.revealStops([eventUuid]);
            resolve(res);
          }).catch((err) => {
            reject(err);
          });
        });
      });
    },
    reverseRoute(routeUuids: string[] | undefined) {
      const appRoot = getAppRoot(self);

      const projectId = appRoot.ui.selectedWorkspace?.id;
      if (!projectId) {
        logglyLogger.log({ error: 'No Project ID', info: `Failed to reverse routes ${routeUuids}` });
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      if (!routeUuids) {
        logglyLogger.log({ error: 'No Route Uuids', info: `Failed to reverse route on project ${projectId}` });
        return Promise.reject(new Error('NO_ROUTE_UUID'));
      }

      return new Promise((resolve, reject) => {
        Sentry.getCurrentHub().withScope((scope) => {
          const transaction = Sentry.startTransaction({ name: 'reverseRoute' });
          scope.setSpan(transaction);
          appRoot.model.mutateReverseRoutes({
            projectId, routeUuids,
          }, qb => qb.actionUuid.actionName).currentPromise().then((res) => {
            transaction.finish();
            appRoot.mapView.revealRoutes(routeUuids);
            resolve(res);
          }).catch((err) => {
            reject(err);
          });
        });
      });
    },
    updateWorkspaceVisit(uuid: string | undefined, eventChanges: PatchProjectVisitDto) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      if (!uuid) {
        return Promise.reject(new Error('NO_ORDER_UUID'));
      }

      // convert updated visit duration to seconds
      if (eventChanges) {
        if (eventChanges.duration !== undefined && eventChanges.duration !== null) {
          eventChanges.duration = convertDurationToSeconds(eventChanges.duration);
        }

        if (eventChanges.phone) {
          eventChanges.phone = formatPhoneWithCountryCode(normalizePhoneNumber(eventChanges.phone));
        }
      }

      return appRoot.model.mutateUpdateProjectVisit({
        projectId,
        uuid,
        eventChanges,
      }, qb => qb
        .planningActionDto(b => b.actionUuid.actionName)
        .visitDto(EditedVisitQuery))
        .currentPromise();
    },
    patchDriver(id: number, driver: PatchDriverDto) {
      return self.mutatePatchDriver({ id, driver }, DriverQuery).currentPromise();
    },
    // resendDriverInvites(driverIds: string[]) {
    // TODO: to fix when tackling resending invites
    // return self.mutateResendDriverInvites({ driverIds }, b => b.sentIds.failedIds).currentPromise();
    // },
    resetWorkspaces() {
      if (!getAppRoot(self).ui.selectedCompanySettingsView) {
        const currentProject = getAppRoot(self).ui.selectedWorkspace;
        if (currentProject) {
          self.projectDtos.clear();
          self.projectDtos.set(String(currentProject.id), currentProject);
        } else {
          self.projectDtos.clear();
        }
      }
    },
    getPublishSummary(routeUuids?: string[]) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;
      const startDate = appRoot.ui.selectedDate;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      if (!startDate) {
        return Promise.reject(new Error('NO_DISPATCH_DATE'));
      }

      return self.queryPlanningNotifications({ projectId: parseInt(projectId, 10), startDate, routeUuids }, PublishSummaryQuery, { fetchPolicy: 'no-cache' }).currentPromise();
    },
    publishSelectedRoutes(routeUuids: string[]) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      if (!routeUuids || routeUuids.length === 0) {
        return Promise.reject(new Error('NO_ROUTEUUIDS'));
      }

      return self.mutatePublishRoutes({ projectId, routeUuids }).currentPromise();
    },
    unpublishSelectedRoutes(routeUuids: string[]) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      if (!routeUuids || routeUuids.length === 0) {
        return Promise.reject(new Error('NO_ROUTEUUIDS'));
      }

      return self.mutateUnpublishRoutes({ projectId, routeUuids }).currentPromise();
    },
    publishAllRoutes() {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;
      const startDate = appRoot.ui.selectedDate;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      if (!startDate) {
        return Promise.reject(new Error('NO_DISPATCH_DATE'));
      }

      return self.mutatePublishAll({ projectId, startDate }).currentPromise();
    },
    getGeocodedAddress(addressInput: string) {
      return self.mutateGeocodeAddress({ address: addressInput }).currentPromise();
    },
    getSuggestions(address: string, geolocation?: IGeolocationDto, projectId?: string) {
      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      return self.mutateGetSuggestions({
        address, geolocation,
      }, qb => qb.id.address.latitude.longitude).currentPromise();
    },
    createMultipleRoutes(count: number, route: CreateRouteDto, routeTemplateName?: string, startDate?: string) {
      const { ui } = getAppRoot(self);
      const projectId = ui.selectedWorkspace?.id;
      const routesStartDate = startDate || ui.selectedDate; // if empty backend uses project date

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      return self.mutateCreateRoutes({
        projectId,
        routes: [{ ...route, startDate: routesStartDate, count }],
        routeTemplateName,
      }, RouteCreatedQuery).currentPromise();
    },
    createRoutes(routes: CreateRouteDto[]) {
      const { ui } = getAppRoot(self);
      const projectId = ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      return self.mutateCreateRoutes({ projectId, routes }, RouteCreatedQuery).currentPromise();
    },
    patchRoute(routeUuid: string, eventChanges: NewPatchRouteDto) {
      const { ui } = getAppRoot(self);
      const projectId = ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      const applyOptimisticHexColor = () => {
        const { hexColor } = eventChanges;
        if (!hexColor) {
          return;
        }

        const route = isValidUuid(routeUuid)
          ? self.routeByUuid(routeUuid)
          : self.routeDtos.get(routeUuid);
        route?.setHexColor(hexColor);
      };

      // A new start or end location extends the route, so its next pan must fit
      // it even if part of the route is still in view (test case 2397). Only the
      // selected route is armed, since that is the route the map follows, and
      // `routeUuid` may be a uuid or an id (see applyOptimisticHexColor). A
      // rejected patch changes no geometry, so it must not leave the flag armed
      // for the next unrelated polyline change.
      const { constraints } = eventChanges;
      const { selectedRoute } = ui;
      const { mapView } = getAppRoot(self);
      const isSelectedRoute = !!selectedRoute
        && (selectedRoute.uuid === routeUuid || selectedRoute.id === routeUuid);
      const forcesPan = isSelectedRoute && !!(constraints?.startLocations || constraints?.endLocations);
      if (forcesPan) {
        mapView.setForcePanRouteId(routeUuid);
      }

      return self.mutateUpdateRoutesConstraints(
        { projectId, routeUuids: [routeUuid], eventChanges },
        planningActionDtoModelPrimitives.toString(),
        applyOptimisticHexColor,
      ).currentPromise().catch((err: unknown) => {
        if (forcesPan) {
          mapView.setForcePanRouteId(undefined);
        }
        throw err;
      });
    },
    assignDriverToRoute(projectId: string, routeUuid: string, driverId: string) {
      return self.mutateAssignDriverToRoute({ projectId, routeUuid, driverId }).currentPromise();
    },
    unassignDriverFromRoute(projectId: string, routeUuid: string) {
      return self.mutateUnassignDriverFromRoute({ projectId, routeUuid }).currentPromise();
    },
    getRoutesRange(startDate: string, endDate: string, limit: number = 50, offset: number = 0) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      // Validate date range to prevent backend errors
      if (!isValidDateRange(startDate, endDate)) {
        return;
      }

      return self.queryRoutesPaginated({
        limit, offset, projectId, startDate, endDate,
      }, RoutesWeekPaginatedQuery).currentPromise();
    },
    getCalendarRoutes(startDate: string, endDate: string) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      if (!isValidDateRange(startDate, endDate)) {
        return;
      }

      return self.queryRoutesCalendarSummary({
        projectId, startDate, endDate,
      }, RoutesCalendarSummaryQuery, { fetchPolicy: 'no-cache' }).currentPromise();
    },
    refreshCalendarRoutes() {
      const appRoot = getAppRoot(self);
      if (appRoot.ui.selectedCalendarView && appRoot.ui.calendarRange) {
        const [rangeStart, rangeEnd] = appRoot.ui.calendarRange;
        return this.getCalendarRoutes(rangeStart, rangeEnd);
      }
      return Promise.resolve();
    },
    fetchRouteByUuid(routeUuid: string) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;
      if (projectId) {
        return self.queryRouteById({ projectId, routeUuid }, RouteOverviewQuery, { fetchPolicy: 'no-cache' });
      }
    },
    fetchRouteById: flow(function* (routeId: string) {
      const projectId = getAppRoot(self).ui.selectedWorkspace?.id;
      if (!projectId || !routeId || Number.isNaN(Number(routeId))) return;

      yield self.queryRouteById(
        { projectId, routeId },
        RouteOverviewQuery,
        { fetchPolicy: 'no-cache' },
      ).currentPromise();
    }),
    getRoutesStats(rangeStart?: string | null, rangeEnd?: string | null) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;
      const startDate = rangeStart || appRoot.ui.selectedDate;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }
      if (!startDate) {
        return Promise.reject(new Error('NO_START_DATE'));
      }

      return self.queryRoutesStats({ projectId, startDate, endDate: rangeEnd }, RoutesStatsQuery, { fetchPolicy: 'no-cache' }).currentPromise();
    },
    getRouteTemplates() {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      return self.queryRouteTemplates({ projectId }, RouteTemplatesQuery, { fetchPolicy: 'no-cache' }).currentPromise();
    },
    getOptimizationPreferences() {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      return self.queryGetOptimizationPreferences({ projectId }, OptimizationPreferencesQuery, { fetchPolicy: 'no-cache' }).currentPromise();
    },

    getRouteTemplatesOverview(projectId: string) {
      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      return self.queryRouteTemplates({ projectId }, RouteTemplatesOverviewQuery, { fetchPolicy: 'no-cache' }).currentPromise();
    },
    deleteScheduledVisits() {
      const appRoot = getAppRoot(self);

      if (appRoot.ui.selectedVisit?.isScheduled) {
        appRoot.ui.selectVisit(null);
      }

      const visitIds = appRoot.scheduledVisits.map(v => v.id);
      visitIds.forEach(id => self.visitDtos.delete(id));
    },
    clearUnscheduledVisits() {
      const appRoot = getAppRoot(self);

      // Clear selected visit if it's an unscheduled visit
      if (appRoot.ui.selectedVisit && !appRoot.ui.selectedVisit.isScheduled) {
        appRoot.ui.selectVisit(null);
      }

      // Get unscheduled visits and clear them from visitDtos
      const unscheduledVisitIds = appRoot.unscheduledVisits.map(v => v.id);
      unscheduledVisitIds.forEach(id => self.visitDtos.delete(id));

      // Clear orders page queried visit IDs
      appRoot.ordersPage.setQueriedVisitIds([]);
    },
    moveOrdersToRoutes(orderUuids:string[], routeUuids:string[], useConstraintsSettings?: boolean) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      if (!routeUuids || routeUuids.length === 0) {
        return Promise.reject(new Error('NO_ROUTE_UUIDS'));
      }

      if (!orderUuids || orderUuids.length === 0) {
        return Promise.reject(new Error('NO_ORDER_UUIDS'));
      }

      return new Promise((resolve, reject) => {
        Sentry.getCurrentHub().withScope((scope) => {
          const transaction = Sentry.startTransaction({ name: 'moveOrdersToRoutes' });
          scope.setSpan(transaction);
          self.mutateMoveOrdersToRoutes({ 
            projectId, 
            targetRouteUuids: routeUuids, 
            orderUuids,
            useConstraintsSettings,
          },
          (qb) => qb
            .actionUuid
            .actionName)
            .currentPromise()
            .then((res) => {
              transaction.finish();
              appRoot.mapView.revealOrders(orderUuids);
              resolve(res);
            }).catch((err) => {
              reject(err);
            });
        });
      });
    },
    optimizeRoutes(routeUuids: string[]) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      if (routeUuids.length < 1) {
        return Promise.reject(new Error('NO_ROUTEUUIDS'));
      }

      return new Promise((resolve, reject) => {
        Sentry.getCurrentHub().withScope((scope) => {
          const transaction = Sentry.startTransaction({ name: 'optimizeRoutes' });
          scope.setSpan(transaction);
          self.mutateOptimizeRoutes({ routeUuids, projectId },
            (qb) => qb
              .actionUuid
              .actionName)
            .currentPromise()
            .then((res) => {
              transaction.finish();
              appRoot.mapView.revealRoutes(routeUuids);
              resolve(res);
            }).catch((err) => {
              reject(err);
            });
        });
      });
    },
    optimizeOrdersToMultipleTargetRoutes(orderUuids: string[], routeUuids: string[], useConstraintsSettings?: boolean, startDate?: string) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }
      if (!orderUuids || orderUuids.length === 0) {
        return Promise.reject(new Error('NO_ORDER_UUIDS'));
      }

      const targetRouteUuids = routeUuids ?? [];
      const resolvedStartDate = startDate ?? appRoot.ui.selectedDate ?? undefined;

      // No target routes means we want the backend to scaffold routes from active
      // route templates. That intent must be sent explicitly via `useTemplates` (with the
      // day to scaffold for) — the backend rejects combining it with target routes.
      let useTemplates: { startDate: string } | undefined;
      if (targetRouteUuids.length === 0) {
        if (!resolvedStartDate) {
          return Promise.reject(new Error('NO_START_DATE'));
        }
        useTemplates = { startDate: resolvedStartDate };
      }

      return new Promise((resolve, reject) => {
        Sentry.getCurrentHub().withScope((scope) => {
          const transaction = Sentry.startTransaction({ name: 'optimizeOrdersToMultipleTargetRoutes' });
          scope.setSpan(transaction);
          self.mutateOptimizeOrdersToMultipleTargetRoutes({ projectId, targetRouteUuids, orderUuids, useConstraintsSettings, useTemplates },
            (qb) => qb
              .actionUuid
              .actionName)
            .currentPromise()
            .then((res) => {
              transaction?.finish();
              // Scaffolding from templates builds new routes; the forced day
              // fit on completion handles the camera there.
              if (targetRouteUuids.length > 0) {
                appRoot.mapView.revealOrders(orderUuids);
              }
              resolve(res);
            }).catch((err) => {
              reject(err);
            });
        });
      });
    },
    getDriversForAssignmentToRoute(routeUuid: string) {
      const appRoot = getAppRoot(self);
      const projectId = Number(appRoot.ui.selectedWorkspace?.id);
      const organizationId = appRoot.ui.selectedWorkspace?.organizationId;

      if (!projectId) {
        throw new Error('NO_PROJECT_ID');
      }
      if (!organizationId) {
        throw new Error('NO_ORGANIZATION_ID');
      }

      return self.queryGetDriversForAssignmentToRoute(
        {
          routeUuid,
          projectId,
          organizationId,
        },
        qb => qb.id.name.speed.familiarityScore(s => s.rating.score).deleted,
        { fetchPolicy: 'cache-and-network' },
      );
    },
    drawRoute(drawnCoordinates: Coordinates[], routeUuid?: string) {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace?.id;

      if (!projectId) {
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }
      if (!routeUuid) {
        return Promise.reject(new Error('NO_ROUTE_UUID'));
      }

      return new Promise((resolve, reject) => {
        Sentry.getCurrentHub().withScope((scope) => {
          const transaction = Sentry.startTransaction({ name: 'drawRoute' });
          scope.setSpan(transaction);
          self.mutateDrawRoute({ routeUuid, projectId, drawnCoordinates },
            (qb) => qb
              .actionUuid
              .actionName)
            .currentPromise()
            .then((res) => {
              transaction.finish();
              resolve(res);
            }).catch((err) => {
              reject(err);
            });
        });
      });
    },
    deleteRoutes: flow(function* (routeUuids: string[]): Generator<Promise<{ deleteRoutes: PlanningActionDtoModelType }>> {
      const appRoot = getAppRoot(self);
      const projectId = appRoot.ui.selectedWorkspace!.id;

      if (routeUuids.length < 1) {
        return Promise.reject(new Error('NO_ROUTEUUIDS'));
      }

      const result = yield self.mutateDeleteRoutes({ routeUuids, projectId }, (qb) => qb
        .actionUuid
        .actionName).currentPromise();

      routeUuids.forEach((uuid) => {
        self.routeCalendarSummaryDtos.delete(uuid);
      });

      return result;
    }),
    getCustomFieldKeys: flow(function* () {
      const result = yield self.queryCustomFields({}, qb => qb.customFields, { fetchPolicy: 'no-cache' }).currentPromise();
      self.customFields = getSnapshot(result.customFields.customFields);
      return result;
    }),
    getUsers(noExistingDriverInvite?: boolean, roles?: RoleNames[]) {
      const queryUsers = async () => {
        const result = await self.queryGetUsers({ noExistingDriverInvite, roles },
          OrgMemberUserQuery, { fetchPolicy: 'no-cache' }).currentPromise();
        return result.getUsers;
      };
      return queryUsers() ?? [];
    },
    updateUserPasswordAsAdmin: flow(function* (email: string, newPassword: string) {
      yield getAppEnv(self).api.put('/admin/user/password', {
        email,
        newPassword,
      });
    }),
    clearTeamMembers() {
      self.teamMemberDtos.clear();
    },
    clearServiceAccounts() {
      self.serviceAccountDtos.clear();
    },
    fetchOrderTimeline: flow(function* (projectId: string, orderUuid: string) {
      self.loadingOrderTimeline = true;

      const queryTimeline = async () => {
        await self.queryOrderTimelineByUuid(
          { projectId, orderUuid },
          OrderTimelineQuery,
          { fetchPolicy: 'network-only' },
        ).currentPromise();
        return Promise.resolve();
      };

      try {
        yield queryTimeline()
          .catch((e) => {
            console.error('Failed to fetch order timeline:', e);
          });
        return Promise.resolve();
      } finally {
        self.loadingOrderTimeline = false;
      }
    }),
    setUnscheduledVisitsLatLngsLoading(loading: boolean) {
      self.isUnscheduledVisitsLatLngsLoading = loading;
    },
  }));

export default Model;
